import { Flags } from "@/providers/LaunchDarklyProvider";
import { useLocalStorageState } from "ahooks";
import { useFlags } from "launchdarkly-react-client-sdk";

export type AgentReasoningEffort = "low" | "standard" | "high" | "x-high";

const DEFAULT_EFFORT: AgentReasoningEffort = "standard";

/** The efforts on offer, highest first: `x-high` only behind its flag. */
export function reasoningEffortOptions(flags: Flags): AgentReasoningEffort[] {
  return flags.extremeReasoningEffort
    ? ["x-high", "high", "standard", "low"]
    : ["high", "standard", "low"];
}

/**
 * Persists the agent reasoning-effort selection under `storageKey` and coerces
 * the hidden `x-high` tier back to default when the flag is off. Scope-agnostic
 * so canvas, workspace chat, etc. can each persist their own selection.
 *
 * `defaultEffort` is the tier used until the user picks one (and the tier
 * a hidden `x-high` falls back to); surfaces that should start higher than
 * "standard" pass their own.
 */
export function useReasoningEffort(
  storageKey: string,
  defaultEffort: AgentReasoningEffort = DEFAULT_EFFORT,
) {
  const flags = useFlags<Flags>();
  const [effort, setEffort] = useLocalStorageState<AgentReasoningEffort>(
    storageKey,
    { defaultValue: defaultEffort },
  );

  const resolved = effort ?? defaultEffort;
  // Coerce x-high back to the default when the flag is off so users that
  // previously selected Extreme aren't stuck on a hidden tier.
  const sanitized =
    !flags.extremeReasoningEffort && resolved === "x-high"
      ? defaultEffort
      : resolved;

  return [sanitized, setEffort] as const;
}

export function canvasReasoningEffortKey(canvasId: string | undefined) {
  return `canvas_reasoning_effort_${canvasId ?? "__none__"}`;
}

export function useCanvasReasoningEffort(canvasId: string | undefined) {
  return useReasoningEffort(canvasReasoningEffortKey(canvasId));
}

/**
 * Seeds a canvas's effort before its composer first mounts, so a session the
 * home started keeps the effort the home sent; a choice already stored for
 * the canvas is left alone.
 */
export function seedCanvasReasoningEffort(canvasId: string, effort: string) {
  const key = canvasReasoningEffortKey(canvasId);
  try {
    if (localStorage.getItem(key) === null) {
      localStorage.setItem(key, JSON.stringify(effort));
    }
  } catch {
    // Storage is a convenience; the picker falls back to its default.
  }
}
